import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { FamilleLivrables } from '../models/famille-livrables.model';
import { Livrable } from '../models/livrable.model';
import { LivrableCreateRequest } from '../requests/livrable-create-request';
import { FamilleLivrablesStore } from '../store/famille-livrables.store';
import { LivrableStore } from '../store/livrable.store';

@Component({
  selector: 'app-form-livrables',
  imports: [
    ButtonAddNewComponent,
    HlmCardImports,
    ReactiveFormsModule,
    HlmFieldImports,
    HlmInputImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-livrables.component.html',
})
export class FormLivrablesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #familleLivrablesStore = inject(FamilleLivrablesStore);
  readonly #livrableStore = inject(LivrableStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly famille = input.required<FamilleLivrables>();

  readonly form = this.#fb.group({
    livrables: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.addFamille();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addFamille(initialValue?: Livrable) {
    const livrable = this.#fb.group({
      nom: [initialValue?.nom, [Validators.required]],
    });

    this.livrables.push(livrable);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouveauxLivrables: LivrableCreateRequest = {
      idFamilleLivrables: this.famille().id,
      livrables: this.livrables.value.filter((x: { nom: string }) => !!x.nom?.trim()),
    };

    if (!nouveauxLivrables.livrables || nouveauxLivrables.livrables.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#livrableStore.créer({
      data: nouveauxLivrables,
      next: () => {
        this.#familleLivrablesStore.trouverParId(this.famille().id);
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des livrables réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouveauxLivrables.livrables),
    });
  }

  alreadyExists(
    nouveauxLivrables: {
      nom: string;
    }[],
  ) {
    const livrablesDeLaFamille = this.#familleLivrablesStore
      .entities()
      .filter((x) => x.id === this.famille().id)
      .flatMap((x) => x.livrables)
      .map((x) => x?.nom);

    const doublonsAvecLivrablesExistants = livrablesDeLaFamille.filter((x) =>
      nouveauxLivrables.map((y) => y.nom).includes(x!),
    );

    this.livrables.controls.forEach((control) => {
      if (
        doublonsAvecLivrablesExistants.includes(control.value.nom) ||
        this.livrables.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });
  }

  get livrables() {
    return this.form.get('livrables') as FormArray;
  }
}
